import React, { useState } from "react";
import {
  Button,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Paper,
  Divider,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import "../../styles/PrintNOR.css";
import { useVoyageContextV2 } from "../../Contexts/VoyageContextV2";
import { useCompanyContext } from "../../Contexts/CompanyContext";
import { renderDeadweightV2 } from "../../print-v2";
import { getVoyageNumber } from "../../utils/domainSelectors";
import { getSelectedCargo, getSelectedTrip } from "../../utils/voyageSelection";

const DeadweightStatement = () => {
  const { selectedTripId, selectedCargoIndex, currentVoyage } =
    useVoyageContextV2();
  const { companyLogo } = useCompanyContext();
  const selectedTrip = getSelectedTrip(currentVoyage, selectedTripId);
  const selectedCargo = getSelectedCargo(
    currentVoyage,
    selectedTripId,
    selectedCargoIndex
  );

  // Nya state för zon och DWT-värden
  const [charterer, setCharterer] = useState("");
  const [zone, setZone] = useState("Summer"); // Default to "Summer"
  const [governingDWT, setGoverningDWT] = useState("");
  const [bunkerConsumption, setBunkerConsumption] = useState("");
  const [bunkers, setBunkers] = useState("");
  const [freshwater, setFreshwater] = useState("");
  const [stores, setStores] = useState("");
  const [ballast, setBallast] = useState("");
  const [slops, setSlops] = useState("");
  const [availableCBM, setAvailableCBM] = useState("");
  const [density, setDensity] = useState("");
  const [maxQuantAccCP, setMaxQuantAccCP] = useState("");
  const [claim, setClaim] = useState("");
  const [claimedDeadweight, setClaimedDeadweight] = useState("");
  const [requestedCargoMT, setRequestedCargoMT] = useState("");
  const [requestedCargoCub, setRequestedCargoCub] = useState("");
  const [basedOnDens, setBasedOnDens] = useState("");
  const [basedOnTemp, setBasedOnTemp] = useState("");
  const [asPer, setAsPer] = useState("");

  // Beräkna total DWT
  const totalAllowedDWT =
    parseFloat(governingDWT || 0) + parseFloat(bunkerConsumption || 0);

  const lossOfDWT =
    parseFloat(bunkers) +
      parseFloat(freshwater) +
      parseFloat(stores) +
      parseFloat(ballast) +
      parseFloat(slops) || "";

  const availableDWT =
    parseFloat(totalAllowedDWT) - parseFloat(lossOfDWT) || "";

  const availableMT = parseFloat(availableCBM) * parseFloat(density) || "";

  const quantBillOfLaden =
    selectedTrip?.operationType === "Loading"
      ? selectedCargo?.activities?.["completed-load"]?.remarks?.shipfig ||
        "Update in SoF"
      : selectedCargo?.activities?.["completed-dischagrge"]?.remarks?.shipfig ||
        "Update in SoF";

  const actualDeadweight =
    parseFloat(claimedDeadweight) - parseFloat(quantBillOfLaden) || "";

  if (!selectedTrip || !selectedCargo) {
    return (
      <Box sx={{ textAlign: "center", marginTop: 4 }}>
        <Typography variant="h4" gutterBottom>
          No trip or cargo data available.
        </Typography>
        <Typography variant="body1">Add that under Current Voyage</Typography>
      </Box>
    );
  }

  // Hantera zonval
  const handleZoneChange = (event) => {
    setZone(event.target.value);
  };
  const handleAsPer = (event) => {
    setAsPer(event.target.value);
  };

  const handleClaimChange = (event) => {
    switch (event.target.value) {
      case "DWT":
        setClaimedDeadweight(availableDWT);
        break;
      case "Cubic":
        setClaimedDeadweight(availableMT);
        break;
      case "Fixed":
        setClaimedDeadweight(maxQuantAccCP);
        break;
      default:
        setClaimedDeadweight("ÄPPLE");
    }
    setClaim(event.target.value);
  };

  const handlePrintButton = () => {
    if (!selectedCargo) {
      alert("Please select a cargo before printing.");
      return;
    }
    renderDeadweightV2({
      currentVoyage,
      selectedTripId,
      selectedCargoIndex,
      companyLogo,
      zone,
      governingDWT,
      bunkerConsumption,
      bunkers,
      freshwater,
      stores,
      ballast,
      slops,
      availableCBM,
      density,
      maxQuantAccCP,
      claim,
      claimedDeadweight,
      requestedCargoMT,
      requestedCargoCub,
      basedOnDens,
      basedOnTemp,
      asPer,
      totalAllowedDWT,
      lossOfDWT,
      availableDWT,
      availableMT,
      quantBillOfLaden,
      actualDeadweight,
      charterer,
    });
  };

  return (
    <div>
      <Typography variant="h4" sx={{ marginBottom: 2 }}>
        Deadweight Statement
      </Typography>

      <Paper
        elevation={3}
        sx={{ padding: 2, boxShadow: "4px 4px 10px 2px rgba(0, 0, 0, 0.3)" }}
      >
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          Voyage Information
        </Typography>
        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell className="print-nor-table-info-label">
                  Vessel
                </TableCell>
                <TableCell>{currentVoyage?.vessel}</TableCell>
                <TableCell className="print-nor-table-info-label">
                  Date
                </TableCell>
                <TableCell>{selectedTrip?.date}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="print-nor-table-info-label">
                  Jetty
                </TableCell>
                <TableCell>{selectedTrip?.jetty || "N/A"}</TableCell>
                <TableCell className="print-nor-table-info-label">
                  Voyage
                </TableCell>
                <TableCell>{getVoyageNumber(currentVoyage)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="print-nor-table-info-label">To</TableCell>
                <TableCell>{selectedTrip?.to}</TableCell>
                <TableCell className="print-nor-table-info-label">
                  Port
                </TableCell>
                <TableCell>{selectedTrip?.from}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="print-nor-table-info-label">
                  Cargo
                </TableCell>
                <TableCell>
                  {selectedCargo?.cargo}{" "}
                  (
                  {selectedCargo?.quantity} {selectedCargo?.unit}
                  )
                </TableCell>
                <TableCell className="print-nor-table-info-label">
                  Charterer
                </TableCell>
                <TableCell>
                  <TextField
                    label="Charterer"
                    variant="outlined"
                    value={charterer}
                    onChange={(e) => setCharterer(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </TableCell>
              </TableRow>
            </TableHead>
          </Table>
        </TableContainer>
      </Paper>

      <FormControl component="fieldset" sx={{ marginTop: 2 }}>
        <FormLabel component="legend">Select Zone</FormLabel>
        <RadioGroup row value={zone} onChange={handleZoneChange}>
          <FormControlLabel value="Summer" control={<Radio />} label="Summer" />
          <FormControlLabel value="Winter" control={<Radio />} label="Winter" />
          <FormControlLabel
            value="Tropical"
            control={<Radio />}
            label="Tropical"
          />
        </RadioGroup>
      </FormControl>

      {/* Fält för Governing DWT och Bunker Consumption */}
      <Box sx={{ marginBottom: 2 }}>
        <TextField
          label="Governing DWT (Mt)"
          variant="outlined"
          value={governingDWT}
          onChange={(e) => setGoverningDWT(e.target.value)}
          fullWidth
          size="small"
          sx={{ marginBottom: 2 }}
        />
        <TextField
          label="Bunker Consumption to Summer/Winter Zone (Mt)"
          variant="outlined"
          value={bunkerConsumption}
          onChange={(e) => setBunkerConsumption(e.target.value)}
          fullWidth
          size="small"
        />
      </Box>

      {/* Total Allowed DWT */}
      <TextField
        label="Total Allowed DWT (Mt)"
        variant="outlined"
        value={totalAllowedDWT}
        disabled
        fullWidth
        size="small"
        sx={{ marginBottom: 2 }}
      />
      <Divider />
      <Box sx={{ marginBottom: 2, marginTop: 2 }}>
        <TextField
          label="Bunkers (Mt)"
          variant="outlined"
          value={bunkers}
          onChange={(e) => setBunkers(e.target.value)}
          fullWidth
          size="small"
          sx={{ marginBottom: 2 }}
        />
        <TextField
          label="Freshwater (Mt)"
          variant="outlined"
          value={freshwater}
          onChange={(e) => setFreshwater(e.target.value)}
          fullWidth
          size="small"
          sx={{ marginBottom: 2 }}
        />
        <TextField
          label="Stores (Mt)"
          variant="outlined"
          value={stores}
          onChange={(e) => setStores(e.target.value)}
          fullWidth
          size="small"
          sx={{ marginBottom: 2 }}
        />
        <TextField
          label="Ballast (Mt)"
          variant="outlined"
          value={ballast}
          onChange={(e) => setBallast(e.target.value)}
          fullWidth
          size="small"
          sx={{ marginBottom: 2 }}
        />
        <TextField
          label="Slops (Mt)"
          variant="outlined"
          value={slops}
          onChange={(e) => setSlops(e.target.value)}
          fullWidth
          size="small"
          sx={{ marginBottom: 2 }}
        />
        <TextField
          label="Loss of DWT due draft restrictions in fixture (Mt)"
          variant="outlined"
          value={lossOfDWT}
          disabled
          fullWidth
          size="small"
          sx={{ marginBottom: 2 }}
        />
      </Box>
      <Divider />
      <Box sx={{ marginBottom: 2, marginTop: 2 }}>
        <TextField
          label="DWT available for cargo (Mt)"
          variant="outlined"
          value={availableDWT}
          disabled
          fullWidth
          size="small"
          sx={{ marginBottom: 2 }}
        />
        <TextField
          label="Cubic available for cargo (m3)"
          variant="outlined"
          value={availableCBM}
          onChange={(e) => setAvailableCBM(e.target.value)}
          fullWidth
          size="small"
          sx={{ marginBottom: 2 }}
        />
        <TextField
          label="at Density"
          variant="outlined"
          value={density}
          onChange={(e) => setDensity(e.target.value)}
          fullWidth
          size="small"
          sx={{ marginBottom: 2 }}
        />
        <TextField
          label="Available MT"
          variant="outlined"
          value={availableMT}
          disabled
          fullWidth
          size="small"
          sx={{ marginBottom: 2 }}
        />
        <TextField
          label="Max Quantity acc to C/P fixture (Mt)"
          variant="outlined"
          value={maxQuantAccCP}
          onChange={(e) => setMaxQuantAccCP(e.target.value)}
          fullWidth
          size="small"
          sx={{ marginBottom: 2 }}
        />
        <TextField
          label="Quantity according to Bill of Lading (Mt)"
          variant="outlined"
          value={quantBillOfLaden}
          disabled
          fullWidth
          size="small"
          sx={{ marginBottom: 2 }}
        />
      </Box>
      <Divider sx={{ marginY: 2 }} />
      <Box sx={{ marginBottom: 2, marginTop: 2 }}>
        <FormControl
          component="fieldset"
          sx={{ marginTop: 2, marginBottom: 2 }}
        >
          <FormLabel component="legend">Deadweight claimed:</FormLabel>
          <RadioGroup row value={claim} onChange={handleClaimChange}>
            <FormControlLabel
              value="DWT"
              control={<Radio />}
              label="DWT available for cargo"
            />
            <FormControlLabel
              value="Cubic"
              control={<Radio />}
              label="Cubic available for cargo"
            />
            <FormControlLabel
              value="Fixed"
              control={<Radio />}
              label="Fixed quantity according to Charterparty"
            />
          </RadioGroup>
        </FormControl>
        <TextField
          label="Deadweight Claimed"
          variant="outlined"
          value={claimedDeadweight}
          disabled
          fullWidth
          size="small"
          sx={{ marginBottom: 2 }}
        />
      </Box>
      <Divider sx={{ marginY: 2 }} />
      <Box sx={{ marginBottom: 2, marginTop: 2 }}>
        <TextField
          label="Deadweight"
          variant="outlined"
          value={actualDeadweight}
          disabled
          fullWidth
          size="small"
          sx={{ marginBottom: 2 }}
        />
      </Box>
      <Divider sx={{ marginY: 2 }} />
      <Box sx={{ marginBottom: 2, marginTop: 2 }}>
        <TextField
          label="Cargo requested prior/upon arrival	(Mt)"
          variant="outlined"
          value={requestedCargoMT}
          onChange={(e) => setRequestedCargoMT(e.target.value)}
          fullWidth
          size="small"
          sx={{ marginBottom: 2 }}
        />
        <TextField
          label="Cargo requested prior/upon arrival	(m3)"
          variant="outlined"
          value={requestedCargoCub}
          onChange={(e) => setRequestedCargoCub(e.target.value)}
          fullWidth
          size="small"
          sx={{ marginBottom: 2 }}
        />
        <TextField
          label="Based on density"
          variant="outlined"
          value={basedOnDens}
          onChange={(e) => setBasedOnDens(e.target.value)}
          fullWidth
          size="small"
          sx={{ marginBottom: 2 }}
        />
        <TextField
          label="Based on Max. Temp"
          variant="outlined"
          value={basedOnTemp}
          onChange={(e) => setBasedOnTemp(e.target.value)}
          fullWidth
          size="small"
          sx={{ marginBottom: 2 }}
        />
        <FormControl
          component="fieldset"
          sx={{ marginTop: 2, marginBottom: 2 }}
        >
          <FormLabel component="legend">As per:</FormLabel>
          <RadioGroup row value={asPer} onChange={handleAsPer}>
            <FormControlLabel
              value="CP"
              control={<Radio />}
              label="C/P terms"
            />
            <FormControlLabel
              value="orders"
              control={<Radio />}
              label="Voy Orders"
            />
          </RadioGroup>
        </FormControl>
      </Box>

      <Divider sx={{ marginY: 2 }} />
      <Button
        variant="contained"
        color="primary"
        startIcon={<PrintIcon />}
        onClick={handlePrintButton}
      >
        Print Deadweight Statement
      </Button>
    </div>
  );
};

export default DeadweightStatement;
